// Day-Diary v2 step-debugger — extraction summary aggregator.
//
// Walks every completed `consolidate.*` step in a session and groups its
// suggestions / entries into a client → project → activity tree, plus
// Internal, Personal, and "newly proposed activities (no project)"
// buckets. Output is purely presentational — the panel component below
// renders the tree as-is without further transformation.
//
// Type-narrowing notes: `DebugStepResponse.parsed` is typed as `unknown`
// because the runner attaches multiple parse shapes across step kinds.
// We only consume `parsed` when `summaryContext` is populated, which the
// runner only attaches after a successful parse — so casts to the
// project / fallback parsed shapes are safe in the branches we enter.
// The shapes mirror `ProjectConsolidateResult` and
// `FallbackConsolidateResult` in `web/src/reports/day-diary/`.

import type { DebugStep, EventMeta, SummaryContext } from '@/hooks/useDebugSession';

// ── Output tree ─────────────────────────────────────────────────────────────

export interface SuggestionRow {
  readonly title: string;
  readonly eventCount: number;
  /** True when this row was emitted as a `new_activity` (renders with a `(new)` pill). */
  readonly isNew: boolean;
  /**
   * Events that contributed to this suggestion, resolved from the step's
   * `events` lookup. May be shorter than `eventCount` when a suggestion
   * referenced ids the runner did not attach metadata for (e.g. backend
   * lookup race / future schema drift) — the renderer falls back to the
   * count-only display in that case.
   */
  readonly events: readonly EventMeta[];
}

export interface ActivityNode {
  readonly name: string;
  /** True when the activity itself was newly proposed by the model. */
  readonly isNew: boolean;
  readonly totalEvents: number;
  readonly suggestions: readonly SuggestionRow[];
}

export interface ProjectNode {
  readonly name: string;
  readonly totalEvents: number;
  readonly activities: readonly ActivityNode[];
}

export interface ClientNode {
  readonly name: string;
  readonly totalEvents: number;
  readonly projects: readonly ProjectNode[];
}

export interface PersonalSection {
  readonly totalEvents: number;
  /** Activity rows routed to Personal (existing Personal activity). At most one in practice. */
  readonly existingActivities: readonly ActivityNode[];
  /** Suggestions emitted with the personal-placeholder forward reference (Personal activity pending creation). */
  readonly placeholderRows: readonly SuggestionRow[];
}

export interface SummaryTree {
  /** Real clients, sorted by `totalEvents` desc. */
  readonly clients: readonly ClientNode[];
  /** Fallback-batch `new_activity` suggestions — no project anchor. Sorted by event count desc. */
  readonly newActivitiesNoProject: readonly SuggestionRow[];
  /** Fallback-batch `unattributable` suggestions. Sorted by event count desc. */
  readonly internal: readonly SuggestionRow[];
  readonly personal: PersonalSection;
  readonly totals: SummaryTotals;
}

export interface SummaryTotals {
  readonly events: number;
  readonly suggestions: number;
  /** Distinct activities attributed (existing ids + distinct new-activity names). */
  readonly activities: number;
  readonly unattributable: number;
}

// ── Parsed-shape mirrors (match backend Zod-validated output) ───────────────

interface ParsedProjectEntry {
  readonly title: string;
  readonly eventIds: readonly string[];
  readonly activityRef:
    | { readonly kind: 'existing'; readonly id: string }
    | { readonly kind: 'new'; readonly name: string; readonly description: string }
    | null;
}

interface ParsedProjectResult {
  readonly entries: readonly ParsedProjectEntry[];
}

interface ParsedFallbackSuggestion {
  readonly title: string;
  readonly eventIds: readonly string[];
  readonly kind: 'existing_activity' | 'new_activity' | 'personal' | 'unattributable';
  readonly activityRef:
    | { readonly kind: 'existing'; readonly id: string }
    | { readonly kind: 'new'; readonly name: string; readonly description: string }
    | { readonly kind: 'personal-placeholder' }
    | null;
}

interface ParsedFallbackResult {
  readonly suggestions: readonly ParsedFallbackSuggestion[];
}

// ── Aggregator ──────────────────────────────────────────────────────────────

/** Sentinel project name when a project consolidate entry has no activity ref. */
const NO_ACTIVITY_NAME = '(no activity)';
/** Activity name displayed when an existing activity id is missing from the lookup. */
const UNKNOWN_ACTIVITY_NAME = '(unknown activity)';
/** Personal-section row label for placeholder suggestions. */
export const PERSONAL_PLACEHOLDER_ROW = 'Personal activity (pending creation)';

/**
 * Build the summary tree from a session's steps. Walks only steps with a
 * populated `summaryContext` (the runner attaches it on successful
 * consolidate parses). Idempotent and pure — same input, same output.
 */
export function buildDaySummary(steps: readonly DebugStep[]): SummaryTree {
  const agg = new TreeAggregator();

  for (const step of steps) {
    if (step.summaryContext === null || step.response === null || step.response.parsed === null) {
      continue;
    }
    if (step.summaryContext.kind === 'consolidate.project') {
      const result = step.response.parsed as ParsedProjectResult;
      for (const entry of result.entries) {
        agg.ingestProjectEntry(entry, step.summaryContext);
      }
    } else {
      const result = step.response.parsed as ParsedFallbackResult;
      for (const sugg of result.suggestions) {
        agg.ingestFallbackSuggestion(sugg, step.summaryContext);
      }
    }
  }

  return agg.build();
}

// The aggregator is a small mutable helper used only inside `buildDaySummary`.
// Keeping it as a class scopes the mutation; the output is fully frozen
// arrays at `build()` time.
class TreeAggregator {
  private readonly clientsByName = new Map<string, MutableClient>();
  private readonly newNoProject = new Map<string, MutableSuggestion>();
  private readonly internal = new Map<string, MutableSuggestion>();
  private readonly personalActivities = new Map<string, MutableActivity>();
  private readonly personalPlaceholderRows: SuggestionRow[] = [];
  private totalEvents = 0;
  private totalSuggestions = 0;
  private totalUnattributable = 0;
  private readonly attributedActivityKeys = new Set<string>();

  ingestProjectEntry(entry: ParsedProjectEntry, ctx: SummaryContext): void {
    const eventCount = entry.eventIds.length;
    const events = resolveEvents(entry.eventIds, ctx);
    this.totalEvents += eventCount;
    this.totalSuggestions += 1;

    // Project consolidates always have a batchProject (the runner sets it).
    const project = ctx.batchProject;
    if (project === null) return; // defensive — shape guarantees non-null on project arm

    if (entry.activityRef === null) {
      this.placeProjectEntry(
        project.clientName,
        project.name,
        NO_ACTIVITY_NAME,
        false,
        entry.title,
        eventCount,
        events,
      );
      return;
    }

    if (entry.activityRef.kind === 'new') {
      this.attributedActivityKeys.add(`new:${project.id}:${entry.activityRef.name}`);
      this.placeProjectEntry(
        project.clientName,
        project.name,
        entry.activityRef.name,
        true,
        entry.title,
        eventCount,
        events,
      );
      return;
    }

    // kind === 'existing'
    const lookup = ctx.activityLookup[entry.activityRef.id];
    const activityName = lookup?.activityName ?? UNKNOWN_ACTIVITY_NAME;
    const clientName = lookup?.clientName ?? project.clientName;
    const projectName = lookup?.projectName ?? project.name;
    this.attributedActivityKeys.add(`existing:${entry.activityRef.id}`);

    if (lookup?.isPersonal) {
      this.placePersonalExisting(activityName, false, entry.title, eventCount, events);
      return;
    }
    this.placeProjectEntry(
      clientName,
      projectName,
      activityName,
      false,
      entry.title,
      eventCount,
      events,
    );
  }

  ingestFallbackSuggestion(sugg: ParsedFallbackSuggestion, ctx: SummaryContext): void {
    const eventCount = sugg.eventIds.length;
    const events = resolveEvents(sugg.eventIds, ctx);
    this.totalEvents += eventCount;
    this.totalSuggestions += 1;

    switch (sugg.kind) {
      case 'unattributable':
        this.totalUnattributable += 1;
        this.mergeSuggestionInto(this.internal, sugg.title, eventCount, false, events);
        return;

      case 'new_activity':
        if (sugg.activityRef?.kind === 'new') {
          this.attributedActivityKeys.add(`new-fallback:${sugg.activityRef.name}`);
        }
        this.mergeSuggestionInto(this.newNoProject, sugg.title, eventCount, true, events);
        return;

      case 'personal':
        if (sugg.activityRef?.kind === 'personal-placeholder') {
          this.personalPlaceholderRows.push({
            title: sugg.title,
            eventCount,
            isNew: false,
            events,
          });
          this.attributedActivityKeys.add('personal-placeholder');
          return;
        }
        if (sugg.activityRef?.kind === 'existing') {
          const lookup = ctx.activityLookup[sugg.activityRef.id];
          const activityName = lookup?.activityName ?? UNKNOWN_ACTIVITY_NAME;
          this.attributedActivityKeys.add(`existing:${sugg.activityRef.id}`);
          this.placePersonalExisting(activityName, false, sugg.title, eventCount, events);
        }
        return;

      case 'existing_activity': {
        if (sugg.activityRef?.kind !== 'existing') return;
        const lookup = ctx.activityLookup[sugg.activityRef.id];
        const activityName = lookup?.activityName ?? UNKNOWN_ACTIVITY_NAME;
        this.attributedActivityKeys.add(`existing:${sugg.activityRef.id}`);

        if (lookup?.isPersonal) {
          this.placePersonalExisting(activityName, false, sugg.title, eventCount, events);
          return;
        }

        // Activity may have no project (and therefore no client). Route to
        // Internal in that edge case so the row still surfaces in the panel.
        if (lookup?.projectName == null || lookup.clientName == null) {
          this.mergeSuggestionInto(this.internal, sugg.title, eventCount, false, events);
          return;
        }
        this.placeProjectEntry(
          lookup.clientName,
          lookup.projectName,
          activityName,
          false,
          sugg.title,
          eventCount,
          events,
        );
        return;
      }
    }
  }

  build(): SummaryTree {
    const clients: ClientNode[] = [...this.clientsByName.values()]
      .map((c) => freezeClient(c))
      .sort(byEventsDesc);

    const newActivitiesNoProject = sortSuggestions([...this.newNoProject.values()]);
    const internal = sortSuggestions([...this.internal.values()]);

    const existingPersonalActivities = [...this.personalActivities.values()]
      .map((a) => freezeActivity(a))
      .sort(byEventsDesc);
    const placeholderTotal = this.personalPlaceholderRows.reduce((n, r) => n + r.eventCount, 0);
    const personalTotal =
      existingPersonalActivities.reduce((n, a) => n + a.totalEvents, 0) + placeholderTotal;

    const personal: PersonalSection = {
      totalEvents: personalTotal,
      existingActivities: existingPersonalActivities,
      placeholderRows: this.personalPlaceholderRows
        .slice()
        .sort((a, b) => b.eventCount - a.eventCount || a.title.localeCompare(b.title))
        .map((r) => ({ ...r, events: sortEvents(r.events) })),
    };

    return {
      clients,
      newActivitiesNoProject,
      internal,
      personal,
      totals: {
        events: this.totalEvents,
        suggestions: this.totalSuggestions,
        activities: this.attributedActivityKeys.size,
        unattributable: this.totalUnattributable,
      },
    };
  }

  // ── Internal placement helpers ────────────────────────────────────────────

  private placeProjectEntry(
    clientName: string,
    projectName: string,
    activityName: string,
    isNew: boolean,
    title: string,
    eventCount: number,
    events: readonly EventMeta[],
  ): void {
    const client = this.ensureClient(clientName);
    const project = ensureProject(client, projectName);
    const activity = ensureActivity(project, activityName, isNew);
    addSuggestion(activity, title, eventCount, isNew, events);
    client.totalEvents += eventCount;
    project.totalEvents += eventCount;
    activity.totalEvents += eventCount;
  }

  private placePersonalExisting(
    activityName: string,
    isNew: boolean,
    title: string,
    eventCount: number,
    events: readonly EventMeta[],
  ): void {
    let activity = this.personalActivities.get(activityName);
    if (!activity) {
      activity = { name: activityName, isNew, totalEvents: 0, suggestions: new Map() };
      this.personalActivities.set(activityName, activity);
    }
    addSuggestion(activity, title, eventCount, isNew, events);
    activity.totalEvents += eventCount;
  }

  private mergeSuggestionInto(
    bucket: Map<string, MutableSuggestion>,
    title: string,
    eventCount: number,
    isNew: boolean,
    events: readonly EventMeta[],
  ): void {
    const existing = bucket.get(title);
    if (existing) {
      existing.eventCount += eventCount;
      if (isNew) existing.isNew = true;
      mergeEventsInto(existing.events, events);
      return;
    }
    bucket.set(title, { title, eventCount, isNew, events: [...events] });
  }

  private ensureClient(name: string): MutableClient {
    const existing = this.clientsByName.get(name);
    if (existing) return existing;
    const fresh: MutableClient = { name, totalEvents: 0, projects: new Map() };
    this.clientsByName.set(name, fresh);
    return fresh;
  }
}

// ── Mutable scaffolding ─────────────────────────────────────────────────────

interface MutableClient {
  name: string;
  totalEvents: number;
  projects: Map<string, MutableProject>;
}
interface MutableProject {
  name: string;
  totalEvents: number;
  activities: Map<string, MutableActivity>;
}
interface MutableActivity {
  name: string;
  isNew: boolean;
  totalEvents: number;
  /** Keyed by suggestion title so cross-batch suggestions with identical titles merge. */
  suggestions: Map<string, MutableSuggestion>;
}
interface MutableSuggestion {
  title: string;
  eventCount: number;
  isNew: boolean;
  /** Accumulated events across cross-batch merges, deduped by id. */
  events: EventMeta[];
}

function ensureProject(client: MutableClient, name: string): MutableProject {
  const existing = client.projects.get(name);
  if (existing) return existing;
  const fresh: MutableProject = { name, totalEvents: 0, activities: new Map() };
  client.projects.set(name, fresh);
  return fresh;
}

function ensureActivity(project: MutableProject, name: string, isNew: boolean): MutableActivity {
  const existing = project.activities.get(name);
  if (existing) {
    // If any batch saw this activity as "new" while another saw it as existing,
    // prefer the existing classification (the activity is real in at least one
    // batch). isNew stays sticky in the opposite direction.
    if (!isNew && existing.isNew) existing.isNew = false;
    return existing;
  }
  const fresh: MutableActivity = { name, isNew, totalEvents: 0, suggestions: new Map() };
  project.activities.set(name, fresh);
  return fresh;
}

function addSuggestion(
  activity: MutableActivity,
  title: string,
  eventCount: number,
  isNew: boolean,
  events: readonly EventMeta[],
): void {
  const existing = activity.suggestions.get(title);
  if (existing) {
    existing.eventCount += eventCount;
    if (isNew) existing.isNew = true;
    mergeEventsInto(existing.events, events);
    return;
  }
  activity.suggestions.set(title, { title, eventCount, isNew, events: [...events] });
}

/**
 * Resolve a suggestion / entry's `eventIds` into the resolved event metadata
 * the panel needs (app, title, url, screenshotId, occurredAt). Missing ids
 * are silently dropped — the suggestion row still renders with its count,
 * but expand-to-events will show fewer rows than `eventCount` (the renderer
 * handles this gracefully).
 */
function resolveEvents(eventIds: readonly string[], ctx: SummaryContext): readonly EventMeta[] {
  const out: EventMeta[] = [];
  for (const id of eventIds) {
    const ev = ctx.events[id];
    if (ev !== undefined) out.push(ev);
  }
  return out;
}

/**
 * Append `incoming` events into `into`, skipping any already present by `id`.
 * Mutates `into` in place. Used when a suggestion title is seen in multiple
 * batches and the events lists need to merge without duplicating rows.
 */
function mergeEventsInto(into: EventMeta[], incoming: readonly EventMeta[]): void {
  const seen = new Set(into.map((e) => e.id));
  for (const e of incoming) {
    if (!seen.has(e.id)) {
      into.push(e);
      seen.add(e.id);
    }
  }
}

// ── Freezing + sorting ──────────────────────────────────────────────────────

function freezeClient(c: MutableClient): ClientNode {
  return {
    name: c.name,
    totalEvents: c.totalEvents,
    projects: [...c.projects.values()].map(freezeProject).sort(byEventsDesc),
  };
}

function freezeProject(p: MutableProject): ProjectNode {
  return {
    name: p.name,
    totalEvents: p.totalEvents,
    activities: [...p.activities.values()].map(freezeActivity).sort(byEventsDesc),
  };
}

function freezeActivity(a: MutableActivity): ActivityNode {
  return {
    name: a.name,
    isNew: a.isNew,
    totalEvents: a.totalEvents,
    suggestions: sortSuggestions([...a.suggestions.values()]),
  };
}

function sortSuggestions(rows: MutableSuggestion[]): SuggestionRow[] {
  return rows
    .slice()
    .sort((a, b) => b.eventCount - a.eventCount || a.title.localeCompare(b.title))
    .map((r) => ({
      title: r.title,
      eventCount: r.eventCount,
      isNew: r.isNew,
      events: sortEvents(r.events),
    }));
}

function sortEvents(events: readonly EventMeta[]): readonly EventMeta[] {
  return events.slice().sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
}

function byEventsDesc<T extends { totalEvents: number; name: string }>(a: T, b: T): number {
  return b.totalEvents - a.totalEvents || a.name.localeCompare(b.name);
}
